import { cn } from '@vta/ui';

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx={11} cy={11} r={7} />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}

/**
 * The "spoof" search input rendered in the header.
 *
 * This element does not capture keystrokes — it is purely a button that opens
 * the CommandPalette. Keeping the palette as the single source of search
 * state avoids the dual-input problem where you'd type here, then have to
 * re-type in the modal.
 *
 * Layout:
 *   - Desktop (≥ md): full pill-shaped bar with placeholder + ⌘K hint.
 *   - Mobile: compresses to a square icon button.
 */
export interface HeaderSearchTriggerProps {
  onClick: () => void;
  className?: string;
}

export function HeaderSearchTrigger({ onClick, className }: HeaderSearchTriggerProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Search teams, cities, countries"
      className={cn(
        'group inline-flex items-center gap-2 rounded-md bg-bg-raised text-fg-muted shadow-flat',
        'transition-colors duration-quick ease-out-expo',
        'hover:text-fg hover:bg-[rgb(var(--bg-overlay))]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg-base',
        // Mobile (icon only)
        'h-9 w-9 justify-center md:w-auto md:justify-start',
        // Desktop (full bar)
        'md:h-9 md:min-w-[18rem] md:px-3 md:py-2',
        className,
      )}
    >
      <SearchIcon className="h-4 w-4 shrink-0" />
      <span className="hidden text-sm md:inline">Search teams, cities…</span>
      <span className="ml-auto hidden items-center gap-1 md:inline-flex">
        <Kbd>⌘</Kbd>
        <Kbd>K</Kbd>
      </span>
    </button>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="rounded-xs border border-border-subtle bg-bg-base px-1.5 py-0.5 font-mono text-2xs text-fg-subtle">
      {children}
    </kbd>
  );
}
